/**
 * Service de calcul du Score Digital Composite Multi-Dimensionnel
 * 
 * Calcule un score global (0-100) basé sur 5 piliers :
 * 1. Performance Technique (25%)
 * 2. Optimisation SEO (25%)
 * 3. Réputation & Confiance (20%)
 * 4. Maturité Digitale (20%)
 * 5. Fiabilité Infrastructure (10%)
 */

import { RankedKeywordData } from '../lib/types';

interface PageSpeedData {
  mobile_performance_score?: number;
  desktop_performance_score?: number;
  accessibility_score?: number;
  best_practices_score?: number;
  seo_score?: number;
}

interface GoogleReviewsData {
  rating?: number;
  reviews_count?: number;
}

interface CmsData {
  cms_name?: string;
  version?: string;
  confidence?: number;
}

interface ConnectivityData {
  isAccessible: boolean;
  responseTime?: number;
  hasSSL?: boolean;
}

interface ScoreBreakdown {
  global: number;
  performance: {
    score: number;
    mobile: number;
    desktop: number;
    accessibility: number;
    weight: number;
  };
  seo: {
    score: number;
    pagespeedSeo: number;
    keywordsCount: number;
    avgPosition: number;
    weight: number;
  };
  reputation: {
    score: number;
    rating: number;
    reviewsCount: number;
    weight: number;
  };
  maturity: {
    score: number;
    technology: number;
    bestPractices: number;
    weight: number;
  };
  infrastructure: {
    score: number;
    accessibility: number;
    security: number;
    weight: number;
  };
}

export class DigitalScoreCalculator {
  
  /**
   * Calcule le score de Performance Technique (25%)
   * Basé sur : Mobile (60%) + Desktop (40%) + Accessibilité (bonus 10%)
   */
  private calculatePerformanceScore(pagespeed?: PageSpeedData): number {
    if (!pagespeed) return 50; // Valeur par défaut si aucune donnée

    const mobile = pagespeed.mobile_performance_score 
      ? Math.round(pagespeed.mobile_performance_score * 100) 
      : null;
    
    const desktop = pagespeed.desktop_performance_score 
      ? Math.round(pagespeed.desktop_performance_score * 100) 
      : null;
    
    const accessibility = pagespeed.accessibility_score 
      ? Math.round(pagespeed.accessibility_score * 100) 
      : 0;

    // Score de base (mobile + desktop)
    let baseScore: number;
    if (mobile !== null && desktop !== null) {
      baseScore = (mobile * 0.6) + (desktop * 0.4);
    } else if (mobile !== null) {
      baseScore = mobile;
    } else if (desktop !== null) {
      baseScore = desktop;
    } else {
      baseScore = 50;
    }

    // Bonus accessibilité (max 10% du score)
    const accessibilityBonus = (accessibility / 100) * 10;
    
    return Math.min(Math.round(baseScore + accessibilityBonus), 100);
  }

  /**
   * Calcule le score SEO (25%)
   * Basé sur : SEO Score PageSpeed (40%) + Mots-clés top 10 (30%) + Volume recherche (20%) + Positions moyennes (10%)
   */
  private calculateSeoScore(
    pagespeed?: PageSpeedData,
    keywords: RankedKeywordData[] = []
  ): number {
    // Score SEO technique de PageSpeed
    const seoBase = pagespeed?.seo_score 
      ? Math.round(pagespeed.seo_score * 100 * 0.4) 
      : 20; // 20/40 par défaut

    // Analyse des mots-clés
    if (keywords.length === 0) {
      return seoBase; // Si pas de keywords, retourne juste le score SEO technique
    }

    const top10Count = keywords.filter(k => k.rank_absolute <= 10).length;
    const top20Count = keywords.filter(k => k.rank_absolute <= 20).length;
    const totalVolume = keywords.reduce((sum, k) => sum + (k.search_volume || 0), 0);
    const avgPosition = keywords.reduce((sum, k) => sum + k.rank_absolute, 0) / keywords.length;

    // Score basé sur les mots-clés en top 10/20 (30 points max)
    const keywordsScore = Math.min(
      ((top10Count * 2) + top20Count) / 30 * 30, // Plus de poids pour top 10
      30
    );

    // Score basé sur le volume de recherche (20 points max)
    const volumeScore = Math.min(
      (totalVolume / 5000) * 20, // Normalisation : 5000+ recherches mensuelles = 20 points
      20
    );

    // Score basé sur la position moyenne (10 points max)
    const positionScore = avgPosition <= 50 
      ? Math.max(10 - (avgPosition / 5), 0) 
      : 0;

    return Math.round(seoBase + keywordsScore + volumeScore + positionScore);
  }

  /**
   * Calcule le score Réputation (20%)
   * Basé sur : Note Google (70%) + Nombre d'avis (30%)
   */
  private calculateReputationScore(reviews?: GoogleReviewsData): number {
    if (!reviews || !reviews.rating) {
      return 50; // Valeur neutre si pas d'avis
    }

    // Score basé sur la note (70 points max)
    const ratingScore = (reviews.rating / 5) * 70;

    // Score basé sur le nombre d'avis (30 points max)
    const reviewsCount = reviews.reviews_count || 0;
    let countScore: number;
    if (reviewsCount >= 100) {
      countScore = 30; // Excellent
    } else if (reviewsCount >= 50) {
      countScore = 25; // Très bien
    } else if (reviewsCount >= 20) {
      countScore = 20; // Bien
    } else if (reviewsCount >= 10) {
      countScore = 15; // Moyen
    } else if (reviewsCount >= 5) {
      countScore = 10; // Faible
    } else {
      countScore = 5; // Très faible
    }

    return Math.round(ratingScore + countScore);
  }

  /**
   * Calcule le score Maturité Digitale (20%)
   * Basé sur : Technologies modernes (50%) + Best Practices (50%)
   */
  private calculateMaturityScore(
    cms?: CmsData,
    pagespeed?: PageSpeedData
  ): number {
    // Score basé sur les technologies (50 points max)
    let technologyScore = 50; // Neutre par défaut

    if (cms?.cms_name) {
      const cmsName = cms.cms_name.toLowerCase();
      
      // Technologies modernes/performantes
      if (cmsName.includes('next') || cmsName.includes('react') || 
          cmsName.includes('vue') || cmsName.includes('nuxt') ||
          cmsName.includes('gatsby') || cmsName.includes('svelte')) {
        technologyScore = 50; // Excellent (frameworks modernes)
      }
      // Technologies établies et maintenues
      else if (cmsName.includes('wordpress') || cmsName.includes('shopify') ||
               cmsName.includes('wix') || cmsName.includes('webflow') ||
               cmsName.includes('squarespace')) {
        technologyScore = 40; // Bien (plateformes établies)
      }
      // Technologies datées ou problématiques
      else if (cmsName.includes('joomla') || cmsName.includes('drupal') ||
               cmsName.includes('magento')) {
        technologyScore = 30; // Moyen
      }
      // Pas de CMS détecté = site custom (peut être bon ou mauvais)
      else if (cmsName.includes('non détecté') || cmsName === '-') {
        technologyScore = 35; // Neutre/Moyen
      }

      // Bonus si version détectée (signe de maintenance)
      if (cms.version && cms.version !== 'unknown') {
        technologyScore = Math.min(technologyScore + 5, 50);
      }
    }

    // Score basé sur les Best Practices (50 points max)
    const bestPracticesScore = pagespeed?.best_practices_score 
      ? Math.round(pagespeed.best_practices_score * 50) 
      : 25; // 25/50 par défaut

    return Math.round(technologyScore + bestPracticesScore);
  }

  /**
   * Calcule le score Infrastructure (10%)
   * Basé sur : Accessibilité (60%) + SSL (40%)
   */
  private calculateInfrastructureScore(connectivity?: ConnectivityData): number {
    if (!connectivity) {
      return 50; // Valeur neutre si pas de données
    }

    // Score accessibilité (60 points max)
    const accessScore = connectivity.isAccessible ? 60 : 0;

    // Score SSL (40 points max)
    const sslScore = connectivity.hasSSL !== false ? 40 : 0; // true ou undefined = OK

    return Math.round(accessScore + sslScore);
  }

  /**
   * Calcule le score digital global et le détail par pilier
   */
  public calculateScore(data: {
    pagespeed?: PageSpeedData;
    keywords?: RankedKeywordData[];
    googleReviews?: GoogleReviewsData;
    cms?: CmsData;
    connectivity?: ConnectivityData;
  }): ScoreBreakdown {
    
    // Calcul des scores par pilier (0-100 chacun)
    const performanceScore = this.calculatePerformanceScore(data.pagespeed);
    const seoScore = this.calculateSeoScore(data.pagespeed, data.keywords);
    const reputationScore = this.calculateReputationScore(data.googleReviews);
    const maturityScore = this.calculateMaturityScore(data.cms, data.pagespeed);
    const infrastructureScore = this.calculateInfrastructureScore(data.connectivity);

    // Poids de chaque pilier
    const WEIGHTS = {
      performance: 0.25,  // 25%
      seo: 0.25,          // 25%
      reputation: 0.20,   // 20%
      maturity: 0.20,     // 20%
      infrastructure: 0.10 // 10%
    };

    // Calcul du score global pondéré
    const globalScore = Math.round(
      (performanceScore * WEIGHTS.performance) +
      (seoScore * WEIGHTS.seo) +
      (reputationScore * WEIGHTS.reputation) +
      (maturityScore * WEIGHTS.maturity) +
      (infrastructureScore * WEIGHTS.infrastructure)
    );

    // Extraction des détails pour chaque pilier
    const mobile = data.pagespeed?.mobile_performance_score 
      ? Math.round(data.pagespeed.mobile_performance_score * 100) 
      : 0;
    const desktop = data.pagespeed?.desktop_performance_score 
      ? Math.round(data.pagespeed.desktop_performance_score * 100) 
      : 0;
    const accessibility = data.pagespeed?.accessibility_score 
      ? Math.round(data.pagespeed.accessibility_score * 100) 
      : 0;

    const keywordsCount = data.keywords?.length || 0;
    const avgPosition = data.keywords && data.keywords.length > 0
      ? Math.round(data.keywords.reduce((sum, k) => sum + k.rank_absolute, 0) / data.keywords.length)
      : 0;

    return {
      global: globalScore,
      performance: {
        score: performanceScore,
        mobile,
        desktop,
        accessibility,
        weight: WEIGHTS.performance
      },
      seo: {
        score: seoScore,
        pagespeedSeo: data.pagespeed?.seo_score 
          ? Math.round(data.pagespeed.seo_score * 100) 
          : 0,
        keywordsCount,
        avgPosition,
        weight: WEIGHTS.seo
      },
      reputation: {
        score: reputationScore,
        rating: data.googleReviews?.rating || 0,
        reviewsCount: data.googleReviews?.reviews_count || 0,
        weight: WEIGHTS.reputation
      },
      maturity: {
        score: maturityScore,
        technology: 0, // Calculé dans maturityScore
        bestPractices: data.pagespeed?.best_practices_score 
          ? Math.round(data.pagespeed.best_practices_score * 100) 
          : 0,
        weight: WEIGHTS.maturity
      },
      infrastructure: {
        score: infrastructureScore,
        accessibility: data.connectivity?.isAccessible ? 100 : 0,
        security: data.connectivity?.hasSSL !== false ? 100 : 0,
        weight: WEIGHTS.infrastructure
      }
    };
  }
}

// Export d'une instance singleton
export const digitalScoreCalculator = new DigitalScoreCalculator();
